import { FC, useCallback, useEffect, useState } from "react";
import { Modal, Stack, Text, Badge } from "@shopify/polaris";

import { useAppDispatch, useAppSelector } from "../../../hooks/redux.js";
import { useAppQuery, useShopQuery, useAllCollectionQuery } from "../../../hooks/index.js";
import { OPEN_MODAL_SYNC, OPEN_MODAL_COLLECTION_WARNING } from "../../../constants/modalTypes.js";
import { closeModal, openModal } from "../../../store/features/modal.js";
import { updateShop } from "../../../store/features/shop.js";

type SynchronizationStatus = "processing" | "completed" | "failed" | "waiting" | "loading";

type TriggerType = "CREATE_ACTION_BUTTON" | "EDIT_ACTION_BUTTON";

interface SynchronizationStatusBadgeProps {
  status: SynchronizationStatus;
}

interface ModalProps {
  isOpen: boolean;
  modalType: string;
  modalData?: {
    triggerType: TriggerType;
  };
};

const SynchronizationStatusBadge: FC<SynchronizationStatusBadgeProps> = ({ status }) => {
  switch (status) {
    case "completed":
      return <Badge progress="complete" status="success">Synchronization completed</Badge>;
    case "processing":
      return <Badge progress="partiallyComplete" status="warning">Synchronization in progress</Badge>;
    case "failed":
      return <Badge progress="incomplete" status="critical">Synchronization failed</Badge>;
    case "loading":
      return <Badge progress="incomplete" status="new">Loading...</Badge>;
    default:
      return <Badge progress="incomplete" status="new">Synchronization waiting</Badge>;
  }
};

const ProductSynchronizationModal = () => {
  const dispatch = useAppDispatch();
  const userUUID = useAppSelector((state) => state.sseSession.userUUID);
  const shop = useAppSelector((state) => state.shop.data);
  const { isOpen, modalType, modalData } = useAppSelector<ModalProps>((state) => state.modal);
  const [syncStatus, setSyncStatus] = useState<SynchronizationStatus>("loading");
  const [isSyncBegined, setSyncBegined] = useState(false);
  const [isButtonTrigerProcessing, setButtonTrigerProcessing] = useState(false);

  const { isFetching: isShopFetching, refetch: refetchShop } = useShopQuery({
    enabled: false,
    onSuccess: (shopData) => {
      dispatch(updateShop(shopData));
    },
  });

  const { isFetching: isSyncFetching, refetch } = useAppQuery<{ status: string }, Error>({
    url: `/api/products/sync?userUUID=${userUUID}`,
    reactQueryOptions: {
      enabled: false,
      onSuccess: () => {
        refetchShop();
        setSyncBegined(true);
      },
      onError: (e) => {
        console.log("e>", e);
        setSyncStatus("failed");
      }
    }
  });

  const { refetch: refetchCollection } = useAllCollectionQuery({
    enabled: false,
    onSuccess: (data: { collection: any }) => {
      if (data?.collection) {
        dispatch(openModal({ modalType: OPEN_MODAL_SYNC }));
      } else {
        dispatch(openModal({ modalType: OPEN_MODAL_COLLECTION_WARNING }));
      }
    },
  });

  const handleSync = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleClose = useCallback(() => {
    dispatch(closeModal());
    setSyncBegined(false);
  }, [dispatch]);

  useEffect(() => {
    if (shop && !shop.productsSynchronization) {
      refetchCollection();
    }
  }, [shop, refetchCollection]);

  useEffect(() => setSyncStatus(shop?.synchronizationStatus || "waiting"), [shop]);

  useEffect(() => {
    if (isOpen && modalType === OPEN_MODAL_SYNC) {
      refetchShop();
    }
  }, [isOpen, modalType, refetchShop]);

  useEffect(() => {
    setButtonTrigerProcessing(
      (modalData?.triggerType === "CREATE_ACTION_BUTTON"
        || modalData?.triggerType === "EDIT_ACTION_BUTTON")
      && syncStatus === "processing"
    );
  }, [modalData, syncStatus]);

  return (
    <Modal
      open={isOpen && modalType === OPEN_MODAL_SYNC}
      onClose={handleClose}
      title={
        <Stack spacing="tight" alignment="center">
          <Text as="h3" variant="headingLg">Products synchronization</Text>
          {isShopFetching ? (
            <SynchronizationStatusBadge status="loading" />
          ) : (
            <SynchronizationStatusBadge status={syncStatus} />
          )}
        </Stack>
      }
      primaryAction={!isButtonTrigerProcessing && syncStatus !== "completed" && {
        content: "Synchronize",
        onAction: handleSync,
        loading: isSyncFetching || isShopFetching,
        disabled: syncStatus === "processing",
      }}
      secondaryActions={
        syncStatus === "completed"
          ? [
            {
              content: "Continue",
              onAction: handleClose,
            },
          ]
          : []
      }
    >
      <Modal.Section>
        <Stack vertical>
          {isButtonTrigerProcessing ? (
            <Text as="p" variant="bodyMd">
              Sorry, you cannot create or edit actions while the Product Synchronization is in progress. Please try again later.
            </Text>
          ) : syncStatus === "completed" ? (
            <Text as="p" variant="bodyMd">
              You are all set! Now you can create a new action.
            </Text>
          ) : (
            <Stack vertical spacing="loose">
              <Text as="p" variant="bodyMd">
                The first time you run it, you need to synchronize items with your store. The more products you have, the longer it will take. Please wait until it is finished.
              </Text>
              {isSyncBegined && (
                <Text as="p" variant="bodySm">
                  Synchronization has started, you can close this window and come back later.
                </Text>
              )}
            </Stack>
          )}
        </Stack>
      </Modal.Section>
    </Modal>
  );
};

export default ProductSynchronizationModal;
